import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { chain, find, isEmpty } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, publishReplay, refCount, filter, distinctUntilChanged } from 'rxjs/operators';
import { CategoriesStorage, Category, LabReport } from '../documents';
import { InvestigatorReportsStorage } from './investigator-reports-storage';
import { Site } from '../sites';
import { routeData } from '../shared/route-helpers';

@Component({
    selector: 'lab-reports',
    template: require('./lab-reports.component.html'),
    styles: [require('./lab-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabReportsComponent {

    public reports: Observable<LabReport[]>;
    public readonly selectedCategory: Observable<Category>;
    public readonly reportsTypes: Observable<string[]>;

    public selectedReportType: string;

    private readonly allReports: Observable<LabReport[]>;
    private readonly selectedReportTypeSubject = new BehaviorSubject<string>(null);

    constructor(
        reportsStorage: InvestigatorReportsStorage,
        route: ActivatedRoute,
        categoriesStorage: CategoriesStorage,
    ) {
        this.selectedCategory = categoriesStorage.siteSpecificCategories
            .pipe(
                map(categories =>
                    find(categories, category => category.isLabReport())
                ));

        const currentSite = route.data
            .pipe(
                map(x => x[routeData.selectedSite] as Site | undefined),
                filter<Site>(x => !!x),
                distinctUntilChanged()
            );

        this.allReports = reportsStorage.loadReports(this.selectedCategory, currentSite)
            .pipe(
                publishReplay(1),
                refCount()
            ) as Observable<LabReport[]>;

        this.reportsTypes = this.allReports
            .pipe(
                map(reports => {
                    const uniqReportsTypes =  chain(reports)
                        .map(report => report.type)
                        .uniq()
                        .value();

                    return uniqReportsTypes;
                }));

        this.reports = combineLatest(
                this.allReports,
                this.selectedReportTypeSubject
            )
            .pipe(
                map(([labReports, selectedReportType]: [LabReport[], string]) => {
                    if (!selectedReportType) {
                        return labReports;
                    }

                    return isEmpty(labReports)
                        ? null
                        : labReports.filter(report => report.type === selectedReportType);
                })
            );
    }

    public onSelectedReportType(selectedReportType: string) {
        this.selectedReportTypeSubject.next(selectedReportType);
    }
}